import { db } from '../firebase';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  DocumentData
} from 'firebase/firestore';
import { VBID } from '../types/vbid';

// 🔧 關鍵修復：停用這個函數，統一使用 vbidIO.ts 中的 generateSafeDocId
// 這個簡化版本會導致文檔 ID 不一致，產生重複文檔
export const generateSafeDocId = (input: string): string => {
  console.error('❌ [firestoreService] generateSafeDocId 已停用！請使用 vbidIO.ts 中的版本');
  console.error('❌ 調用來源:', new Error().stack);
  
  // 🔧 臨時：返回輸入值，不做任何轉換（避免產生 safe_ 前綴）
  // 但應該盡快替換為 vbidIO.generateSafeDocId
  if (!input) {
    console.error('❌ generateSafeDocId 收到空值！');
    return `doc_${Date.now()}`;
  }
  
  // ⚠️ 警告：這只是臨時方案，應該使用 vbidIO.generateSafeDocId
  return String(input)
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .substring(0, 1500);
};

// VBID CRUD
export const saveVBID = async (vbid: VBID): Promise<void> => {
  await addDoc(collection(db, 'vbids'), vbid);
};

export const updateVBIDFlag = async (userId: string, path: string, numberKey: string, flag: 'on' | 'off'): Promise<void> => {
  const vbidsRef = collection(db, 'vbids');
  const q = query(
    vbidsRef,
    where('userId', '==', userId),
    where('path', '==', path),
    where('timecode', '==', numberKey)
  );
  const querySnapshot = await getDocs(q);
  for (const document of querySnapshot.docs) {
    const docRef = doc(db, 'vbids', document.id);
    await updateDoc(docRef, { flag });
  }
};

export const fetchVBIDs = async (userId: string, mode: string, path: string): Promise<VBID[]> => {
  const vbidsRef = collection(db, 'vbids');
  const q = query(
    vbidsRef,
    where('userId', '==', userId),
    where('mode', '==', mode),
    where('path', '==', path),
    where('flag', 'in', ['on', 'off'])
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => doc.data() as VBID);
};

// Generic helpers for other collections
export const saveDocument = async (collectionName: string, data: DocumentData) => {
  try {
    // 如果是 videos 或 sharedVbids 集合，且有 id 字段，使用 id 作為文件 ID
    if ((collectionName === 'videos' || collectionName === 'sharedVbids') && data.id) {
      // 驗證文檔 ID 是否安全
      const safeId = generateSafeDocId(String(data.id));
      console.log(`🔧 保存到 ${collectionName}，使用安全的文檔 ID: ${safeId}`);
      const docRef = doc(db, collectionName, safeId);
      await setDoc(docRef, data);
    } else if ((collectionName === 'videos' || collectionName === 'sharedVbids') && data.videoId) {
      // 如果沒有 id 字段但有 videoId，使用 videoId 作為文件 ID（向後兼容）
      const safeVideoId = generateSafeDocId(String(data.videoId));
      console.log(`🔧 保存到 ${collectionName}，使用安全的 videoId: ${safeVideoId}`);
      const docRef = doc(db, collectionName, safeVideoId);
      await setDoc(docRef, data);
    } else {
      // 其他情況使用自動生成的 ID
      console.log(`🔧 保存到 ${collectionName}，使用自動生成的 ID`);
      await addDoc(collection(db, collectionName), data);
    }
  } catch (error) {
    console.error(`❌ 保存到 ${collectionName} 失敗:`, error);
    throw error;
  }
};

export const updateDocument = async (collectionName: string, docId: string, data: DocumentData) => {
  const docRef = doc(db, collectionName, docId);
  await updateDoc(docRef, data);
};

export const fetchDocuments = async (collectionName: string, conditions: [string, any, any][]) => {
  let q: any = collection(db, collectionName);
  if (conditions.length > 0) {
    q = query(q, ...conditions.map(([field, op, value]) => where(field, op, value)));
  }
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => doc.data());
};

export const getDocument = async (collectionName: string, docId: string) => {
  const docRef = doc(db, collectionName, docId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? docSnap.data() : null;
};

export const deleteDocument = async (collectionName: string, docId: string) => {
  const docRef = doc(db, collectionName, docId);
  await deleteDoc(docRef);
}; 